import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { AdviceService, CropFieldAdvice, TreatmentType } from '@next/arable-lib/advice';
import { ProductTypeEnum, StateOfMatterEnum } from '@next/arable-lib/crop-aid';
import { CropFieldService, loadArableAdviceCropFields } from '@next/arable-lib/crop-field';
import { CropFieldType } from '@next/arable-lib/shared';
import { CoreActions } from '@next/core-lib';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { CartActions } from '@next/core-lib/cart';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { UserActions, UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  autoSaveCustomerProductStock,
  autoSaveCustomerProductStockSuccess,
  createFertilizerOrder,
  createFertilizerOrderSuccess,
  createOrder,
  createOrderSuccess,
  deleteCropField,
  deleteCropFieldFailed,
  deleteCropFieldSuccess,
  deleteOrderLine,
  deleteOrderLineFailure,
  deleteOrderLineSuccess,
  editCustomerProductStock,
  getCropAids,
  getCropAidsByIds,
  getPackages,
  loadAutomaticAdvice,
  loadAutomaticAdviceFailure,
  loadAutomaticAdviceSuccess,
  loadCompanySettings,
  loadCompanySettingsFailure,
  loadCompanySettingsSuccess,
  loadCropFieldAdvices,
  loadCropFieldAdvicesFailure,
  loadCropFieldAdvicesSuccess,
  loadCropVarietiesByCropIdRequest,
  loadCropVarietiesByCropIdSuccess,
  loadCustomerOrderPlan,
  loadCustomerOrderPlanFailure,
  loadCustomerOrderPlanSuccess,
  loadExistingCropFields,
  loadExistingCropFieldsFailure,
  loadExistingCropFieldsSuccess,
  loadFertilizerOrderLines,
  loadFertilizerOrderLinesFailure,
  loadFertilizerOrderLinesSuccess,
  loadFertilizerOrderPlan,
  loadFertilizerOrderPlanFailure,
  loadFertilizerOrderPlanSuccess,
  loadGBMOrderLines,
  loadGBMOrderLinesFailure,
  loadGBMOrderLinesSuccess,
  loadHasAnyFinalizedOrderLines,
  loadHasAnyFinalizedOrderLinesFailure,
  loadHasAnyFinalizedOrderLinesSuccess,
  loadOrderLines,
  loadOrderLinesFailure,
  loadOrderLinesSuccess,
  loadSalesOrderPeriods,
  loadSalesOrderPeriodsFailure,
  loadSalesOrderPeriodsSuccess,
  openConfirmFertilizerOrderDialog,
  openFertilizerProposalDialog,
  openOrderPlanSummary,
  optimizeOrderPlan,
  saveCustomerProductStock,
  saveFertilizerStock,
  saveOrderLine,
  saveOrderLineFailure,
  saveOrderLineSuccess,
  setCropAidPackages,
  setCropAids,
  setPackages,
  upsertCropField,
  upsertCropFieldFailed,
  upsertCropFieldSuccess,
} from './actions';
import { cropYearChanged } from '../../store/actions';
import { AdviceState } from './feature';
import {
  selectCropVarietiesByCropId,
  selectCustomerOrderPlan,
  selectFertilizerOrderLines,
  selectFertilizerOrderPlan,
  selectOrderLines,
} from './selectors';
import { selectManualCropAids, selectPackages } from '../../advices/store/selectors';
import { NexusAIIdentifiers } from '../../dashboard/shared/constants';
import { AutomaticAdviceApiService } from '../../shared/automatic-advice/services/automatic-advice-api.service';
import {
  CropAid,
  CustomerOrderPlan,
  FertilizerOrderPlanResponse,
  OrderLineStatus,
  OrderPlanCropAidDisplayData,
  SourceEnum,
} from '../../shared/models';
import { ArableCustomerService } from '../../shared/services/arable-customer.service';
import { CropVarietyService } from '../../shared/services/crop-variety.service';
import { OrderplanningService } from '../../shared/services/orderplanning.service';
import { ConfirmOrderDialogOverviewComponent } from '../components/confirm-order-dialog/confirm-order-dialog.component';
import { ConfirmOrderDialogFertilizerComponent } from '../components/confirm-order-dialog-fertilizer/confirm-order-dialog-fertilizer.component';
import { CustomerProductInventoryDialogComponent } from '../components/customer-product-inventory-dialog/customer-product-inventory-dialog.component';
import { FertilizerProposalDialogComponent } from '../components/fertilizer-proposal-dialog/fertilizer-proposal-dialog.component';
import { CropFieldService as LocalCropFieldService } from '../services/crop-field.service';
import { selectCropYear } from '../../store/selectors';

@Injectable()
export class AdviceEffects {
  protected store = inject<Store<AdviceState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly cropVarietyService = inject(CropVarietyService);
  private readonly localCropFieldService = inject(LocalCropFieldService);
  private readonly orderplanningService = inject(OrderplanningService);
  private readonly arableCustomerService = inject(ArableCustomerService);
  private readonly automaticAdviceApiService = inject(AutomaticAdviceApiService);
  private readonly adviceService = inject(AdviceService);
  private readonly cropFieldService = inject(CropFieldService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly platformID = inject<Object>(PLATFORM_ID);
  private readonly selectedCropYear$ = this.store.pipe(select(selectCropYear));

  locationInitialized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationInitialized),
      filter(() => isPlatformBrowser(this.platformID)),
      mergeMap(() =>
        this.selectedCropYear$.pipe(
          take(1),
          mergeMap((cropYear) => [
            loadGBMOrderLines({ shouldOpenOrderPlanSummary: false }),
            loadFertilizerOrderLines({ shouldOpenOrderPlanSummary: false }),
            loadHasAnyFinalizedOrderLines({ year: cropYear }),
            loadCropFieldAdvices({ cropYear }),
            loadSalesOrderPeriods({ year: cropYear }),
            getCropAids(),
          ]),
        ),
      ),
    ),
  );

  cropYearChanged$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cropYearChanged),
      mergeMap(({ cropYear }) => [
        loadAutomaticAdvice({ cropYear }),
        loadCompanySettings({ year: cropYear }),
        loadHasAnyFinalizedOrderLines({ year: cropYear }),
        loadCropFieldAdvices({ cropYear }),
        loadArableAdviceCropFields({ cropYear, cropFieldType: CropFieldType.Advice }),
        loadSalesOrderPeriods({ year: cropYear }),
        loadGBMOrderLines({ shouldOpenOrderPlanSummary: false }),
        loadFertilizerOrderLines({ shouldOpenOrderPlanSummary: false }),
        loadFertilizerOrderPlan({ year: cropYear }),
      ]),
    ),
  );

  loadCompanySettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCompanySettings),
      switchMap(({ payload: { year } }) =>
        this.arableCustomerService.getCompanySettings(year).pipe(
          map((response) => loadCompanySettingsSuccess({ companySettings: response })),
          catchError(() => of(loadCompanySettingsFailure({ message: 'errors.loadCompanySettingsErrorMessage' }))),
        ),
      ),
    ),
  );

  loadCropFieldAdvices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCropFieldAdvices),
      switchMap(({ payload: { cropYear } }) =>
        this.adviceService.getCropFieldAdvicesByCropYear(cropYear).pipe(
          switchMap((cropFieldAdvices: CropFieldAdvice[]) => [loadCropFieldAdvicesSuccess({ cropFieldAdvices })]),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError(() => of(loadCropFieldAdvicesFailure({ message: 'errors.getCropFieldAdvicesErrorMessage' }))),
        ),
      ),
    ),
  );

  loadAutomaticAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAutomaticAdvice),
      switchMap(({ payload: { cropYear } }) =>
        this.automaticAdviceApiService.getAutomaticAdvice(cropYear).pipe(
          map((response) => loadAutomaticAdviceSuccess({ automaticAdvice: response })),
          catchError(() => of(loadAutomaticAdviceFailure({ message: 'errors.getAutomaticAdviceErrorMessage' }))),
        ),
      ),
    ),
  );

  loadCropVarietiesByCropId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCropVarietiesByCropIdRequest),
      withLatestFrom(this.store.select(selectCropVarietiesByCropId)),
      mergeMap(([{ cropIds }, cropVarietiesByCropIds]) => {
        const missingCropIds = cropIds.filter((cropId) => !cropVarietiesByCropIds?.some((x) => x.cropId === cropId));

        if (missingCropIds.length === 0) {
          return of(
            loadCropVarietiesByCropIdSuccess({
              cropIds,
              cropVarieties: cropVarietiesByCropIds,
            }),
          );
        }

        return this.cropVarietyService.getGroupedCropVarietiesByCropIds(missingCropIds).pipe(
          map((cropVarietiesResults) => {
            return loadCropVarietiesByCropIdSuccess({
              cropIds: missingCropIds,
              cropVarieties: cropVarietiesResults,
            });
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  loadExistingCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadExistingCropFields),
      switchMap(({ payload }) =>
        this.cropFieldService.getCropFields(payload.cropYear, CropFieldType.Advice).pipe(
          map((existingCropFields) => loadExistingCropFieldsSuccess({ existingCropFields })),
          catchError(() => of(loadExistingCropFieldsFailure({ message: 'errors.loadExistingCropFieldsErrorMessage' }))),
        ),
      ),
    ),
  );

  upsertCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(upsertCropField),
      withLatestFrom(this.selectedCropYear$),
      mergeMap(([action, cropYear]) =>
        this.localCropFieldService.upsertAutomaticAdviceCropFields(action.cropField).pipe(
          map((_) => {
            this.store.dispatch(
              loadArableAdviceCropFields({
                cropYear,
                cropFieldType: CropFieldType.Advice,
              }),
            );
            return upsertCropFieldSuccess();
          }),
          catchError((_) => of(upsertCropFieldFailed())),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    ),
  );

  deleteManualCrop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCropField),
      withLatestFrom(this.selectedCropYear$),
      mergeMap(([action, cropYear]) =>
        this.localCropFieldService.deleteCropField(action.cropFieldId).pipe(
          map((_) => {
            this.store.dispatch(
              loadArableAdviceCropFields({
                cropYear,
                cropFieldType: CropFieldType.Advice,
              }),
            );
            return deleteCropFieldSuccess({ cropFieldId: action.cropFieldId });
          }),
          catchError((_) => of(deleteCropFieldFailed())),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    ),
  );

  loadHasAnyFinalizedOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadHasAnyFinalizedOrderLines),
      switchMap(({ payload: { year } }) =>
        this.orderplanningService.hasFinalizedAnyOrderLine(year).pipe(
            map((hasAnyFinalizedOrderLines: boolean) =>
              loadHasAnyFinalizedOrderLinesSuccess({ hasAnyFinalizedOrderLines }),
            ),
            catchError(() => of(loadHasAnyFinalizedOrderLinesFailure({ message: 'errors.getOrderLinesErrorMessage' }))),
          ),
      ),
    ),
  );

  getCustomerOrderPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCustomerOrderPlan),
      switchMap((_) =>
        this.orderplanningService.getCustomerOrderPlan().pipe(
          mergeMap((customerOrderPlan) => of(loadCustomerOrderPlanSuccess({ customerOrderPlan }))),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError(() => of(loadCustomerOrderPlanFailure({ message: 'errors.getCustomerOrderPlanErrorMessage' }))),
        ),
      ),
    ),
  );

  getSalesOrderPeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSalesOrderPeriods),
      switchMap(({ payload: { year } }) =>
        this.orderplanningService.getSalesOrderPeriods(year).pipe(
          mergeMap((salesOrderPeriods) => of(loadSalesOrderPeriodsSuccess({ salesOrderPeriods }))),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError(() => of(loadSalesOrderPeriodsFailure({ message: 'errors.getSalesOrderPeriodsErrorMessage' }))),
        ),
      ),
    ),
  );

  openFertilizerProposalDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openFertilizerProposalDialog),
        switchMap(() =>
          this.selectedCropYear$.pipe(
            take(1),
            tap((cropYear) => this.store.dispatch(loadFertilizerOrderPlan({ year: cropYear }))),
            switchMap(() => this.store.pipe(select(selectFertilizerOrderPlan))),
            filter((fertilizerOrderPlan) => !!fertilizerOrderPlan),
            take(1),
            switchMap((fertilizerOrderPlan) => {
              const formattedOrderPlan = this.formatFertilizerOrderPlan(fertilizerOrderPlan!);

              return this.dialog
                .form(FertilizerProposalDialogComponent, {
                  orderPlan: formattedOrderPlan,
                  title: this.locale.translate('manualCrops.fertilizerProposal'),
                  confirmButton: true,
                  confirmButtonText: this.locale.translate('report.putInShoppingCart'),
                  pushHistoryState: false,
                  cancelButtonText: this.locale.translate('close'),
                })
                .pipe(
                  switchMap((result) => {
                    if (result && result.status === DIALOG_RETURN_TYPES.confirm) {
                      this.ai.trackEvent(NexusAIIdentifiers.order, { type: 'fertilizer' });
                      this.store.dispatch(openConfirmFertilizerOrderDialog());
                    }
                    return EMPTY;
                  }),
                );
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  editCustomerProductInventoryForOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editCustomerProductStock),
      withLatestFrom(this.store.pipe(select(selectCustomerOrderPlan))),
      switchMap(([_, orderPlan]) => {
        const orderplanByCropAids = this.mapOrderPlanV2ForCustomerProductInventory(orderPlan);
        const sortedOrderplanByCropAids = orderplanByCropAids.sort((a, b) => {
          if (a.cropAid.name > b.cropAid.name) return 1;
          if (a.cropAid.name < b.cropAid.name) return -1;
          return 0;
        });
        return this.dialog
          .form(CustomerProductInventoryDialogComponent, {
            orderplanByCropAids: sortedOrderplanByCropAids,
            title: this.locale.translate('report.editCustomerProductInventory'),
            confirmButton: !!(orderplanByCropAids && orderplanByCropAids.length),
            confirmButtonText: this.locale.translate('report.putInShoppingCart'),
            pushHistoryState: false,
          })
          .pipe(
            // @ts-ignore
            switchMap(({ status }) => {
              switch (status) {
                case DIALOG_RETURN_TYPES.cancel:
                  return EMPTY;
                case DIALOG_RETURN_TYPES.confirm:
                  return of(openOrderPlanSummary());
              }
            }),
          );
      }),
    ),
  );

  openConfirmFertilizerOrderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openConfirmFertilizerOrderDialog),
        withLatestFrom(
          this.store.select(selectFertilizerOrderPlan),
          this.store.select(selectPackages),
          this.store.select(selectFertilizerOrderLines),
        ),
        tap(([_, fertilizerOrderPlan, packages, orderLines]) => {
          const cartLineCount = orderLines.filter((ol) => ol.status === OrderLineStatus.inCart).length;

          this.dialog.fullscreenDialog(ConfirmOrderDialogFertilizerComponent, {
            fertilizerOrderPlan,
            packages,
            cartLineCount,
            title: this.locale.translate('report.checkOrder'),
            confirmButton:
              orderLines.some((ol) => ol.status === 'Open' && ol.count > 0) &&
              fertilizerOrderPlan?.orderableProducts.some((p) => p.amount > 0),
            confirmButtonText: this.locale.translate('report.confirmOrder'),
            disableConfirmButton: cartLineCount > 0,
          });
        }),
      ),
    { dispatch: false },
  );

  saveCustomerProductStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveCustomerProductStock),
      switchMap((action) =>
        this.orderplanningService.upsertCustomerProductInventory(new Date().getFullYear(), action.result).pipe(
          map(() => optimizeOrderPlan()),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  autoSaveCustomerProductStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(autoSaveCustomerProductStock),
      debounceTime(500),
      switchMap((action) =>
        this.orderplanningService.upsertCustomerProductInventory(new Date().getFullYear(), action.productInventories).pipe(
          mergeMap(() => [
            optimizeOrderPlan(),
            autoSaveCustomerProductStockSuccess({ productInventories: action.productInventories }),
          ]),
          catchError((_) => {
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  saveFertilizerStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveFertilizerStock),
      switchMap((action) => {
        return this.orderplanningService.upsertFertilizerStock(action.cropYear, action.fertilizerStock).pipe(
          mergeMap(() => [
            loadFertilizerOrderPlan({ year: action.cropYear }),
            loadFertilizerOrderLines({ shouldOpenOrderPlanSummary: false }),
            loadGBMOrderLines({ shouldOpenOrderPlanSummary: false }),
            loadCustomerOrderPlan(),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  optimizeOrderPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(optimizeOrderPlan),
      switchMap((_) =>
        this.orderplanningService.optimizeOrderPlan().pipe(
          mergeMap(() => [
            loadGBMOrderLines({ shouldOpenOrderPlanSummary: false }),
            loadFertilizerOrderLines({ shouldOpenOrderPlanSummary: false }),
            loadCustomerOrderPlan(),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getGBMOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadGBMOrderLines),
      withLatestFrom(this.selectedCropYear$),
      switchMap(
        ([
          {
            payload: { shouldOpenOrderPlanSummary },
          },
          cropYear,
        ]) =>
        this.orderplanningService.getOrderLines(cropYear, TreatmentType.GBM).pipe(
          mergeMap((orderLines) => [
            loadGBMOrderLinesSuccess({ orderLines }),
            ...(shouldOpenOrderPlanSummary ? [openOrderPlanSummary()] : []),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          catchError(() => of(loadGBMOrderLinesFailure({ message: 'errors.getOrderLinesErrorMessage' }))),
        ),
      ),
    ),
  );

  getFertilizerOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFertilizerOrderLines),
      withLatestFrom(this.selectedCropYear$),
      switchMap(([, cropYear]) =>
        this.orderplanningService.getOrderLines(cropYear, TreatmentType.Fertilization).pipe(
          map((orderLines) => loadFertilizerOrderLinesSuccess({ orderLines })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          catchError(() =>
            of(loadFertilizerOrderLinesFailure({ message: 'errors.getFertilizerOrderLinesErrorMessage' })),
          ),
        ),
      ),
    ),
  );

  getFertilizerOrderPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFertilizerOrderPlan),
      switchMap(({ payload: { year } }) =>
        this.orderplanningService.getFertilizerOrderPlan(year).pipe(
          map((orderPlan) => loadFertilizerOrderPlanSuccess({ orderPlan })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          catchError(() =>
            of(loadFertilizerOrderPlanFailure({ message: 'errors.getFertilizerOrderPlanErrorMessage' })),
          ),
        ),
      ),
    ),
  );

  // Legacy effect for backward compatibility
  getOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOrderLines),
      withLatestFrom(this.selectedCropYear$),
      switchMap(
        ([
          {
            payload: { shouldOpenOrderPlanSummary },
          },
          cropYear,
        ]) =>
        this.orderplanningService.getOrderLines(cropYear, TreatmentType.GBM).pipe(
          mergeMap((orderLines) => [
            loadOrderLinesSuccess({ orderLines }),
            ...(shouldOpenOrderPlanSummary ? [openOrderPlanSummary()] : []),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          catchError(() => of(loadOrderLinesFailure({ message: 'errors.getOrderLinesErrorMessage' }))),
        ),
      ),
    ),
  );

  confirmOrderForOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openOrderPlanSummary),
      withLatestFrom(
        this.store.select(selectManualCropAids),
        this.store.select(selectPackages),
        this.store.select(selectOrderLines),
      ),
      switchMap(([_, cropAids, packages, orderLines]) => {
        const cartLineCount = orderLines.filter((ol) => ol.status === OrderLineStatus.inCart).length;
        return this.dialog
          .fullscreenDialog(ConfirmOrderDialogOverviewComponent, {
            cropAids,
            packages,
            cartLineCount,
            title: this.locale.translate('report.orderOverview'),
            confirmButton: orderLines.some((ol) => ol.status === 'Open' && ol.count > 0),
            confirmButtonText: this.locale.translate('report.confirmOrder'),
            disableConfirmButton: cartLineCount > 0,
          })
          .pipe(
            // @ts-ignore
            switchMap(({ status }) => {
              switch (status) {
                case DIALOG_RETURN_TYPES.cancel:
                  return EMPTY;
                case DIALOG_RETURN_TYPES.confirm:
                  return of(createOrder());
              }
            }),
          );
      }),
    ),
  );

  getPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPackages),
      switchMap((_) =>
        this.orderplanningService.getPackages().pipe(
          map((packages) => setPackages({ packages })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getCropAids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCropAids),
      switchMap((_) =>
        this.orderplanningService.getCropAids().pipe(
          map((cropAidResponse) => {
            const sourceMap = {
              0: SourceEnum.OneSAP,
              1: SourceEnum.AgrifirmArableLegacySAP,
              2: SourceEnum.Manual,
            };

            const cropAids = cropAidResponse.map(
              (cropAid) =>
                ({
                  id: cropAid.id,
                  productId: cropAid.productV2Id ?? cropAid.productId ?? 0,
                  name: cropAid.name,
                  isActive: cropAid.isActive,
                  unitOfMeasurement: cropAid.unitOfMeasurement,
                  codes: cropAid.cropProtection?.admissions?.map((admission) => admission.number) ?? [],
                  cropProtection: cropAid.cropProtection,
                  source: sourceMap[cropAid.source as keyof typeof sourceMap] ?? SourceEnum.Manual,
                  productType: cropAid.productType === 'Fertilizer' ? ProductTypeEnum.Fertilizer : ProductTypeEnum.GBM,
                  indicativePrice: cropAid.indicativePrice ?? 0,
                  isCropProtection: cropAid.isCropProtection ?? false,
                  isFertilizer: cropAid.isFertilizer ?? false,
                  stateOfMatter:
                    cropAid.stateOfMatter === 'Liquid' ? StateOfMatterEnum.Liquid : StateOfMatterEnum.Solid,
                  isNutrition: cropAid.isNutrition ?? false,
                  forbiddenForPlanetProof: cropAid.forbiddenForPlanetProof ?? false,
                  isHidden: cropAid.isHidden ?? false,
                }) as CropAid,
            );
            return setCropAids({ cropAids });
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getCropAidsByIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCropAidsByIds),
      switchMap((action) =>
        this.orderplanningService.getCropAidsByIds(action.cropAidIds).pipe(
          map((cropAidPackages) => {
            return setCropAidPackages({ cropAidPackages });
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createOrder),
      withLatestFrom(
        this.store.pipe(select(UserSelectors.selectCustomer)),
        this.store.pipe(select(UserSelectors.selectLocation)),
      ),
      switchMap(([_, customer, location]) => {
        this.ai.trackEvent(NexusAIIdentifiers.putInCart, {});
        return this.orderplanningService.createOrder(new Date().getFullYear()).pipe(
          map((_) => createOrderSuccess({ customerId: customer.id, locationId: location.id })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  createFertilizerOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createFertilizerOrder),
      withLatestFrom(
        this.store.pipe(select(UserSelectors.selectCustomer)),
        this.store.pipe(select(UserSelectors.selectLocation)),
      ),
      switchMap(([action, customer, location]) => {
        this.ai.trackEvent(NexusAIIdentifiers.putInCart, {});
        return this.orderplanningService.createFertilizerOrder(new Date().getFullYear(), action.deliveryDate).pipe(
          map((_) => createFertilizerOrderSuccess({ customerId: customer.id, locationId: location.id })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  createOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createOrderSuccess),
      switchMap(({ customerId, locationId }) =>
        this.dialog
          .confirm({
            title: this.locale.translate('report.successfullyOrdered'),
            confirmButtonText: this.locale.translate('report.openCart'),
            confirmButtonIcon: true,
            confirmButtonIconType: 'shopping_cart',
            cancelButtonText: this.locale.translate('report.returnToOverview'),
          })
          .pipe(
            filter((result) => result.status === DIALOG_RETURN_TYPES.confirm),
            switchMap(() => {
              const returnUrl = window.location.href;
              return of(CartActions.openCartUrl({ customerId, locationId, returnUrl })).pipe(delay(100));
            }),
          ),
      ),
    ),
  );

  createFertilizerOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createFertilizerOrderSuccess),
      switchMap(({ customerId, locationId }) =>
        this.dialog
          .confirm({
            title: this.locale.translate('report.successfullyOrdered'),
            confirmButtonText: this.locale.translate('report.openCart'),
            confirmButtonIcon: true,
            confirmButtonIconType: 'shopping_cart',
            cancelButtonText: this.locale.translate('report.returnToOverview'),
          })
          .pipe(
            filter((result) => result.status === DIALOG_RETURN_TYPES.confirm),
            switchMap(() => {
              const returnUrl = window.location.href;
              return of(CartActions.openCartUrl({ customerId, locationId, returnUrl })).pipe(delay(100));
            }),
          ),
      ),
    ),
  );

  deleteOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteOrderLine),
      switchMap((action) =>
        this.orderplanningService.deleteOrderLine(action.payload.orderLineId.toString(), action.payload.year).pipe(
          map(() => deleteOrderLineSuccess({ orderLineId: action.payload.orderLineId })),
          catchError((error) =>
            of(deleteOrderLineFailure({ message: error.message || 'Failed to delete order line' })),
          ),
        ),
      ),
    ),
  );

  deleteOrderLineSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteOrderLineSuccess),
      withLatestFrom(this.selectedCropYear$),
      mergeMap(([, cropYear]) => [
        loadFertilizerOrderLines({ shouldOpenOrderPlanSummary: false }),
        loadGBMOrderLines({ shouldOpenOrderPlanSummary: false }),
        loadCustomerOrderPlan(),
        loadFertilizerOrderPlan({ year: cropYear }),
      ]),
    ),
  );

  saveOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveOrderLine),
      switchMap((action) =>
        this.orderplanningService.saveOrderLine(action.payload.orderLine).pipe(
          map((savedOrderLine) => saveOrderLineSuccess({ orderLine: savedOrderLine })),
          catchError((error) => of(saveOrderLineFailure({ message: error.message || 'Failed to save order line' }))),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveOrderLineSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveOrderLineSuccess),
      withLatestFrom(this.selectedCropYear$),
      mergeMap(([, cropYear]) => [
        loadFertilizerOrderLines({ shouldOpenOrderPlanSummary: false }),
        loadGBMOrderLines({ shouldOpenOrderPlanSummary: false }),
        loadCustomerOrderPlan(),
        loadFertilizerOrderPlan({ year: cropYear }),
      ]),
    ),
  );
  handleError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSalesOrderPeriodsFailure),
      map(({ payload: { message } }) => {
        return NotificationActions.errorSnackbar({
          message: this.locale.translate(message),
        });
      }),
    ),
  );

  private formatFertilizerOrderPlan(fertilizerOrderPlan: FertilizerOrderPlanResponse): OrderPlanCropAidDisplayData[] {
    if (!fertilizerOrderPlan) {
      return [];
    }

    const allProducts = [...fertilizerOrderPlan.orderableProducts, ...fertilizerOrderPlan.nonOrderableProducts];

    return allProducts.map(
      (product) =>
        ({
          cropAid: {
            id: product.cropAidId,
            name: product.productName || 'Unknown Product',
            unitOfMeasurement: product.cropAidUnit || 'KG',
          } as CropAid,
          customerProductInventoryAmount: product.stockAmount || 0,
          orderLinesTotalAmount: product.amount || 0,
          requisiteAmount: product.requisiteAmount || 0,
        }) as OrderPlanCropAidDisplayData,
    );
  }

  private mapOrderPlanV2ForCustomerProductInventory(
    customerOrderPlan: CustomerOrderPlan[],
  ): OrderPlanCropAidDisplayData[] {
    return customerOrderPlan
      .filter((orderPlan) => orderPlan.isMainCropAid || !orderPlan.isPackage)
      .map(
        (orderplan) =>
          ({
            cropAid: {
              id: orderplan.cropAidId,
              name: orderplan.cropAidName,
              unitOfMeasurement: orderplan.unitOfMeasurement,
            } as CropAid,
            customerProductInventoryAmount: orderplan.customerProductInventoryAmount,
            orderLinesTotalAmount: orderplan.orderLinesTotalAmount,
            requisiteAmount: orderplan.isMainCropAid
              ? customerOrderPlan.find((op) => op.cropAidId === orderplan.cropAidId && op.isPackage)?.requisiteAmount
              : orderplan.requisiteAmount,
          }) as unknown as OrderPlanCropAidDisplayData,
      )
      .filter((orderplan) => orderplan.requisiteAmount > 0);
  }
}
